import React, { useMemo, useState } from 'react';
import { useVblForecastCalculation } from '@/hooks/useVblForecastCalculation';
import { useLayout } from '@/contexts';
import { DEFAULT_FAILURE_FILTERS } from '@/types/filters';
import { VblForecastFilters, DEFAULT_VBL_FORECAST_FILTERS, VblForecastResult } from '@/types/vblForecast';
import { PageToolbar, WaveLoading, StatsCard, ResponsiveGrid } from '@/components/ui';
import { VblForecastResultsTable } from '@/components/vblForecast/VblForecastResultsTable';
import { VblForecastGanttChart } from '@/components/vblForecast/VblForecastGanttChart';
import { VblForecastSidebarController } from '@/components/vblForecast/VblForecastSidebarController';
import { type VblForecastCISettings } from '@/components/vblForecast/VblForecastSettingsSidebar';

// Default configuration settings
const DEFAULT_CI_SETTINGS: VblForecastCISettings = {
  confidenceLevel: 0.90,
  useSurvivalAdaptation: true,
  survivalAdaptationStrength: 0.3,
  earlyStageThreshold: 0.4
};
import {
  ExclamationTriangleIcon,
  ClockIcon,
  ShieldCheckIcon
} from '@heroicons/react/24/outline';
import { Soundwave } from 'react-bootstrap-icons';

// Default auto-configuration for forecasting until failure
const DEFAULT_AUTO_CONFIG = {
  horizonDays: 3650, // 10 years (effectively until failure)
  intervalDays: 30,
  startDate: new Date().toISOString(),
  useBootstrapSimulation: true,
  confidenceLevel: 0.90,
  includeConfidenceIntervals: true,
  bootstrapSamples: 10000, // Match global default for stable convergence

  // VBL Method settings - Use Monte Carlo for probabilistic results
  useSimplePercentile: false, // Secondary method (available but not used)
  useMonteCarloVBL: true, // Primary method for enhanced forecasting

  // Monte Carlo VBL settings
  samplingMethod: 'hybrid' as const, // Best balance of accuracy and efficiency
  rejectionSampleLimit: 1000, // Reasonable limit for rejection sampling
  useLogNormalScale: true, // Better parameter constraint handling
  correlationThreshold: 0.1, // Use multivariate sampling for low correlation
};

/**
 * Simplified VBL Forecast Page - Automatic forecasting dashboard
 *
 * Features:
 * - Automatic calculations for all turbines requiring forecast that have VBL mappings
 * - Stats cards showing key metrics
 * - Gantt chart visualizing failure timelines with confidence intervals
 * - Comprehensive forecast table with all relevant details
 */
const VblForecastPage: React.FC = () => {
  const { getContentAreaStyle } = useLayout();
  const [ciSettings, setCISettings] = useState<VblForecastCISettings>(DEFAULT_CI_SETTINGS);
  const [filters, setFilters] = useState<VblForecastFilters>(DEFAULT_VBL_FORECAST_FILTERS);
  const [_configurationOverride] = useState(DEFAULT_AUTO_CONFIG);

  // Apply CI settings to configuration when they change
  const mergedConfiguration = useMemo(() => ({
    ..._configurationOverride,
    confidenceLevel: ciSettings.confidenceLevel,
    useSurvivalAdaptation: ciSettings.useSurvivalAdaptation,
    survivalAdaptationStrength: ciSettings.survivalAdaptationStrength,
    earlyStageThreshold: ciSettings.earlyStageThreshold
  }), [_configurationOverride, ciSettings]);

  // Auto-calculate forecasts for all eligible turbines
  const {
    turbinesToForecast: _turbinesToForecast,
    workspaceMappings: _workspaceMappings,
    workspaceParameters: _workspaceParameters,
    forecastResults,
    isLoadingData,
    isCalculating,
    calculationProgress,
    progressText,
    summaryStats,
    dataError,
    calculateForecast
  } = useVblForecastCalculation({
    filters: DEFAULT_FAILURE_FILTERS,
    configuration: mergedConfiguration,
    autoCalculate: true // Enable automatic calculation
  });

  // Generate available filter options from results
  const availableFilterOptions = useMemo(() => {
    if (!forecastResults || forecastResults.length === 0) {
      return {
        sites: [],
        turbineModels: [],
        componentsMajor: [],
        componentsMinor: []
      };
    }

    const sites = Array.from(new Set(forecastResults.map(r => r.turbine.site_name))).sort();
    const turbineModels = Array.from(new Set(forecastResults.map(r => r.turbine.model_name))).sort();
    const componentsMajor = Array.from(new Set(forecastResults.map(r => r.turbine.major_component_name).filter(Boolean))).sort();
    const componentsMinor = Array.from(new Set(forecastResults.map(r => r.turbine.component_name).filter(Boolean))).sort();

    return { sites, turbineModels, componentsMajor, componentsMinor };
  }, [forecastResults]);

  // Handle CI settings apply
  const handleCISettingsApply = () => {
    // Trigger recalculation with new settings
    calculateForecast();
  };

  // Shared deduplication logic for all components (table, Gantt chart, stats)
  const deduplicatedResults = useMemo(() => {
    const createUniqueKey = (result: VblForecastResult) => {
      return `${result.turbine.turbine_id}-${result.turbine.major_component_name || 'unknown'}-${result.turbine.component_name || 'unknown'}`;
    };

    const mapped = forecastResults.filter(r => r.hasMapping && r.predictedFailureDate);

    const deduplicated = mapped.reduce((acc, result) => {
      const key = createUniqueKey(result);
      const existing = acc.get(key);

      if (!existing || new Date(result.predictedFailureDate) < new Date(existing.predictedFailureDate)) {
        acc.set(key, result);
      }

      return acc;
    }, new Map());

    return Array.from(deduplicated.values());
  }, [forecastResults]);

  // Enhanced statistics for the simplified dashboard
  const enhancedStats = useMemo(() => {
    if (!summaryStats) return null;

    // Use deduplicated results for accurate statistics
    const mapped = deduplicatedResults;

    // Risk breakdown
    const highRisk = mapped.filter(r => r.riskLevel === 'High').length;
    const mediumRisk = mapped.filter(r => r.riskLevel === 'Medium').length;
    const lowRisk = mapped.filter(r => r.riskLevel === 'Low').length;

    // Find soonest failure
    const soonestFailure = mapped.length > 0
      ? mapped.reduce((min, r) =>
        r.timeToFailure.days < min.days
          ? { days: Math.round(r.timeToFailure.days), turbine: r.turbine.turbine_name }
          : min,
        { days: Infinity, turbine: '' }
      )
      : null;

    return {
      ...summaryStats,
      forecastsGenerated: mapped.length,
      highRisk,
      mediumRisk,
      lowRisk,
      soonestFailure
    };
  }, [summaryStats, deduplicatedResults]);

  // Error display
  if (dataError) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <ExclamationTriangleIcon className="mx-auto h-12 w-12 text-red-500" />
          <h3 className="mt-2 text-sm font-medium text-gray-900">Failed to load VBL forecast data</h3>
          <p className="mt-1 text-sm text-gray-500">{(dataError as Error)?.message || 'Unknown error'}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      {/* Simplified Page Header */}
      <PageToolbar
        title="VBL Forecast Dashboard"
        icon={Soundwave}
        buttons={[]} // No buttons needed for automatic forecasting
      />

      {/* Main Content Layout */}
      <div className="flex flex-1 overflow-hidden">
        {/* Dual Sidebar System */}
        <VblForecastSidebarController
          settings={ciSettings}
          onSettingsChange={setCISettings}
          filters={filters}
          onFiltersChange={setFilters}
          availableOptions={availableFilterOptions}
          onApply={handleCISettingsApply}
        />

        {/* Main Content Area */}
        <div className="flex-1 overflow-y-auto min-w-0">
          <div style={getContentAreaStyle()}>
            {/* Stats Cards Dashboard - Always visible with loading state */}
            <div className="mb-6">
              <h2 className="text-lg font-medium text-gray-900 mb-4">Forecast Overview</h2>
              <ResponsiveGrid>
                <StatsCard
                  title="Components to Forecast"
                  value={enhancedStats?.forecastsGenerated ?? (isCalculating ? 'Calculating...' : '...')}
                  description="Turbine-components with ongoing issues requiring forecast"
                  color="bright-blue"
                  loading={isLoadingData}
                />
                <StatsCard
                  title="High Risk"
                  value={enhancedStats?.highRisk ?? (isCalculating ? 'Calculating...' : '...')}
                  description="Failure ≤30 days"
                  icon={<ExclamationTriangleIcon className="h-5 w-5 text-red-500" />}
                  color="purple"
                  loading={isLoadingData}
                />
                <StatsCard
                  title="Medium Risk"
                  value={enhancedStats?.mediumRisk ?? (isCalculating ? 'Calculating...' : '...')}
                  description="Failure 31-90 days"
                  icon={<ClockIcon className="h-5 w-5 text-yellow-500" />}
                  color="dark-blue"
                  loading={isLoadingData}
                />
                <StatsCard
                  title="Low Risk"
                  value={enhancedStats?.lowRisk ?? (isCalculating ? 'Calculating...' : '...')}
                  description="Failure >90 days"
                  icon={<ShieldCheckIcon className="h-5 w-5 text-green-500" />}
                  color="green"
                  loading={isLoadingData}
                />
                <StatsCard
                  title="Soonest Projected Failure"
                  value={enhancedStats?.soonestFailure ? `${enhancedStats.soonestFailure.days} days` : (isCalculating ? 'Calculating...' : 'N/A')}
                  description={enhancedStats?.soonestFailure ? enhancedStats.soonestFailure.turbine : (isCalculating ? 'Finding soonest failure...' : 'No forecasts available')}
                  color="fleet-monitor-blue"
                  loading={isLoadingData}
                />
              </ResponsiveGrid>
            </div>


            {/* Main Content - Always show content area */}
            <div className="space-y-6">
              {/* Progress bar during calculations */}
              {isCalculating && (
                <div className="bg-white rounded-lg border p-6">
                  <div className="flex items-center justify-center py-4">
                    <div className="text-center">
                      <WaveLoading text={progressText} />
                      <div className="mt-4 w-64 mx-auto">
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-sm text-gray-600">Calculating forecasts...</span>
                          <span className="text-sm text-gray-500">{Math.round(calculationProgress)}%</span>
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-2">
                          <div
                            className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                            style={{ width: `${calculationProgress}%` }}
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Gantt Chart - Real or Placeholder */}
              {isCalculating ? (
                /* Gantt Chart Placeholder */
                <div className="bg-white rounded-lg border p-6">
                  <div className="flex items-center justify-between mb-4">
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900">Failure Timeline Gantt Chart</h3>
                      <p className="text-sm text-gray-500">Generating timeline visualization...</p>
                    </div>
                    <div className="animate-pulse w-6 h-6 bg-gray-300 rounded"></div>
                  </div>
                  <div className="space-y-3">
                    <div className="animate-pulse h-8 bg-gray-200 rounded"></div>
                    <div className="animate-pulse h-8 bg-gray-100 rounded"></div>
                    <div className="animate-pulse h-8 bg-gray-200 rounded"></div>
                    <div className="animate-pulse h-8 bg-gray-100 rounded"></div>
                  </div>
                </div>
              ) : forecastResults.length > 0 ? (
                /* Real Gantt Chart */
                <VblForecastGanttChart
                  results={deduplicatedResults}
                  configuration={mergedConfiguration}
                  filters={filters}
                />
              ) : null}

              {/* Results Table - Real or Placeholder */}
              {isCalculating ? (
                /* Table Placeholder */
                <div className="bg-white rounded-lg border p-6">
                  <div className="flex items-center justify-between mb-4">
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900">VBL Forecast Results</h3>
                      <p className="text-sm text-gray-500">Calculating forecast predictions...</p>
                    </div>
                    <div className="animate-pulse w-6 h-6 bg-gray-300 rounded"></div>
                  </div>
                  <div className="space-y-3">
                    <div className="animate-pulse h-4 bg-gray-200 rounded w-full"></div>
                    <div className="animate-pulse h-4 bg-gray-100 rounded w-3/4"></div>
                    <div className="animate-pulse h-4 bg-gray-200 rounded w-5/6"></div>
                    <div className="animate-pulse h-4 bg-gray-100 rounded w-2/3"></div>
                    <div className="animate-pulse h-4 bg-gray-200 rounded w-4/5"></div>
                  </div>
                </div>
              ) : forecastResults.length > 0 ? (
                /* Real Results Table */
                <VblForecastResultsTable
                  results={deduplicatedResults}
                  configuration={mergedConfiguration}
                  filters={filters}
                />
              ) : enhancedStats ? (
                /* Empty State */
                <div className="text-center py-12">
                  <div className="bg-white rounded-lg border p-8">
                    <Soundwave className="mx-auto h-16 w-16 text-gray-400" />
                    <h3 className="mt-4 text-lg font-medium text-gray-900">No VBL Forecasts Available</h3>
                    <div className="mt-2 text-sm text-gray-500 max-w-md mx-auto">
                      {enhancedStats.totalTurbines === 0 ? (
                        <p>No turbines with detected degradation found.</p>
                      ) : enhancedStats.mappedTurbines === 0 ? (
                        <p>
                          Found {enhancedStats.totalTurbines} turbines with detected degradation, but none have valid VBL component mappings.
                          Configure VBL mappings in the VBL Config page to enable forecasting.
                        </p>
                      ) : (
                        <p>
                          Unable to generate forecasts for the {enhancedStats.mappedTurbines} mapped turbines.
                          Check that VBL analysis workspaces contain valid parameters.
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              ) : null}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default VblForecastPage;